import type {
  PaginatedResponse,
  EventIdeaResource,
  CreateEventIdeaRequest,
  UpdateEventIdeaRequest,
  ReportEventIdeaResponse,
  EventResponse,
} from '@@/types/api';
import { useApi } from './use-api';

// List params: date-range plus a geo bounding-box (min/max lat/lng over the
// denormalized location columns) plus pagination. Unlike the informational feed
// there is no `event_kind[]` array param, so every key is a plain scalar and
// ofetch serializes it without bracket notation.
interface EventIdeaListParams {
  date_from?: string;
  date_to?: string;
  min_lat?: number;
  max_lat?: number;
  min_lng?: number;
  max_lng?: number;
  page?: number;
  per_page?: number;
}

interface MyEventIdeaListParams {
  page?: number;
  per_page?: number;
}

export function useEventIdeasApi() {
  const { get, post, patch, publicGet } = useApi('/api/v1/event-ideas');

  // The author's own ideas live on a sibling collection, not a sub-path of
  // `/event-ideas` — so they need their own `useApi` instance.
  const mine = useApi('/api/v1/my-event-ideas');

  return {
    list: (params?: EventIdeaListParams) =>
      publicGet<PaginatedResponse<EventIdeaResource>>('', {
        params: params as Record<string, unknown>,
      }),
    // DETAIL is a wrapped single resource (`{ data: {...} }`) — unwrap `.data`
    // here so callers get the bare resource, unlike the community `getEvent`.
    get: async (id: string): Promise<EventIdeaResource> => {
      const res = await publicGet<{ data: EventIdeaResource }>(`/${id}`);
      return res.data;
    },

    // Same endpoint as `get`, but WITH the bearer token — the pair mirrors
    // `getEvent` / `getEventAuthenticated`. Both exist on purpose:
    //
    //   `get` (public) keeps `/ideas/:id` server-renderable, cacheable, and
    //   crawlable. It is the only option during SSR anyway — the access token
    //   lives in `localStorage`, which the server can never read.
    //
    //   `getAuthenticated` is what resolves VIEWER-SPECIFIC fields, above all
    //   `is_own`, which gates every author affordance (promote / edit /
    //   withdraw) and suppresses `report`. An unauthenticated request always
    //   comes back `is_own: false`, so any surface that renders author actions
    //   must read them off THIS call, not off the public/SSR payload.
    getAuthenticated: async (id: string): Promise<EventIdeaResource> => {
      const res = await get<{ data: EventIdeaResource }>(`/${id}`);
      return res.data;
    },

    // ---------------------------------------------------------------------
    // Authoring — all authenticated, all single-resource (`{ data }` unwrapped)
    // ---------------------------------------------------------------------

    // 201 on success. 403 when the author's email is unverified; 422 with a
    // bare `{ message }` (no `errors` bag) when the 5-active cap is hit.
    create: async (
      body: CreateEventIdeaRequest,
    ): Promise<EventIdeaResource> => {
      const res = await post<{ data: EventIdeaResource }>('', { body });
      return res.data;
    },

    // Partial update — `location_hint`/`timing` are all-or-nothing sub-objects.
    update: async (
      id: string,
      body: UpdateEventIdeaRequest,
    ): Promise<EventIdeaResource> => {
      const res = await patch<{ data: EventIdeaResource }>(`/${id}`, { body });
      return res.data;
    },

    // `active` → `withdrawn`. Author-only; no request body.
    withdraw: async (id: string): Promise<EventIdeaResource> => {
      const res = await post<{ data: EventIdeaResource }>(`/${id}/withdraw`);
      return res.data;
    },

    // The one CROSS-AGGREGATE method here: `promote` seeds a draft EVENT from
    // the idea and returns THAT event, not an idea. `EventResource::$wrap` is
    // null, so the body arrives BARE — this is the ONLY method in this client
    // that must NOT unwrap `.data`. Author-only, `active`-only, and idempotent:
    // 201 on the first promotion, 200 returning the SAME draft afterwards.
    // The idea itself stays `active`; the caller only needs `.id` to route into
    // the normal event edit flow.
    promote: (id: string) => post<EventResponse>(`/${id}/promote`),

    // Report someone else's active idea. NOT `{ data }`-wrapped — the backend
    // returns a bare `{ reported: true }` with a 201.
    report: (id: string, reason: string) =>
      post<ReportEventIdeaResponse>(`/${id}/report`, { body: { reason } }),

    // Author's own ideas across ALL statuses, newest-first. Returns the
    // paginated envelope untouched (the list convention).
    myIdeas: (params?: MyEventIdeaListParams) =>
      mine.get<PaginatedResponse<EventIdeaResource>>('', {
        params: params as Record<string, unknown>,
      }),
  };
}
